import { useState, useContext } from "react";

import {
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  isThisWeek,
  isYesterday,
} from "date-fns";
import { BiSolidUser } from "react-icons/bi";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { IoIosLogOut } from "react-icons/io";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { FaUserCircle } from "react-icons/fa";
import { ProfileContext } from "../context/ProfileContext";
import { currentCountry } from "../utils/country";

const formatTime = (raw) => {
  if (typeof raw !== "string") return "";

  const date = new Date(
    raw.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3").replace(" ", "T")
  );

  const now = new Date();

  if (isNaN(date)) return "";

  const mins = differenceInMinutes(now, date);
  const hours = differenceInHours(now, date);
  const weeks = differenceInWeeks(now, date);
  const months = differenceInMonths(now, date);
  const years = differenceInYears(now, date);

  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  if (isYesterday(date)) return "Yesterday";
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, "EEEE");
  if (weeks < 2) return "Last week";
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

const sidebarTranslations = {
  MN: { profile: "Профайл", logout: "Гарах" },
  UZ: { profile: "Profil", logout: "Chiqish" },
  KZ: { profile: "Профиль", logout: "Выход" },
};

const st = sidebarTranslations[currentCountry];

export const Sidebar = ({
  conversations,
  changeConversation,
  cId,
  jId,
  setIsSidebarOpen,
  isSidebarOpen,
}) => {
  const [search, setSearch] = useState("");
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const navigate = useNavigate();
  const { profile, clearProfile } = useContext(ProfileContext);

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    changeConversation(
      parseInt(conversation.companyId),
      parseInt(conversation.jobId)
    );
  };

  // Sort conversations by date descending (newest first)
  // API now returns createdAt field from Firestore document timestamps
  const getDateValue = (conv) => {
    const dateStr = conv.createdAt || conv.updatedAt;
    if (dateStr) {
      return new Date(dateStr).getTime();
    }
    return 0;
  };

  const sortedConversations = conversations
    ? [...conversations].sort((a, b) => getDateValue(b) - getDateValue(a))
    : [];

  const filteredConversations = sortedConversations.filter((c) =>
    c.jobName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full bg-white border-r h-screen flex flex-col py-4 justify-between">
      <div className="flex flex-col md:px-4 px-2">
        <div className="flex items-center justify-between pb-4 ">
          <button
            onClick={() => setIsSidebarOpen(false)}
            title="close sidebar"
            className={`p-1 hover:bg-[#000] hover:bg-opacity-10 rounded-full ${
              !isSidebarOpen && "hidden"
            }`}
          >
            <IoIosArrowForward className="text-[24px]" />
          </button>
          <button
            title="search chats"
            onClick={() => setShowSearchPopup(true)}
            className="px-3 p-2 hover:bg-[#000] hover:bg-opacity-10 rounded-xl text-[22px]"
          >
            <FaMagnifyingGlass />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sortedConversations.map((c, index) => (
            <div
              key={index}
              onClick={() => handleConversationClick(c)}
              className={`flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100 gap-2 rounded-2xl ${
                cId === c.companyId && jId === c.jobId
                  ? "bg-[#000] bg-opacity-20"
                  : selectedConversation?.jobId === c.jobId
                  ? "bg-[#000] bg-opacity-20"
                  : ""
              }`}
            >
              {c.companyPhoto ? (
                <div className="w-10 h-10 rounded-full mr-3 relative border border-[#ccc]">
                  <img
                    src={c.companyPhoto}
                    alt={c.companyName}
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
              ) : (
                <div className="h-10 w-10 border rounded-full flex items-center justify-center">
                  <BiSolidUser className="m-0 cursor-pointer w-[30px] h-[30px] text-[#666874]" />
                </div>
              )}

              <div className="flex-1 gap-2">
                <p
                  title={c.jobName}
                  className="font-semibold text-sm md:text-base max-w-[170px] md:max-w-[190px] truncate"
                >
                  {c.jobName}
                </p>
                <p
                  className="text-[10px] md:text-sm text-gray-500 max-w-[170px] md:max-w-[190px] truncate"
                  title={c.companyName}
                >
                  {c.companyName}
                </p>
              </div>

              <span className="text-[8px] md:text-xs text-gray-400">
                {formatTime(new Date())}
              </span>
            </div>
          ))}
        </div>
      </div>

      {showSearchPopup && (
        <div
          className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50"
          onClick={() => setShowSearchPopup(false)}
        >
          <div
            className="bg-white rounded-xl w-[90%] md:w-[40%] max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 py-3 border-b px-5">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Messenger..."
                className="w-full md:text-base text-sm focus:outline-none"
              />

              <button
                className="md:px-3 ps-3 px-0 p-2 hover:bg-[#000] hover:bg-opacity-10 rounded-xl text-[22px]"
                onClick={() => setShowSearchPopup(false)}
              >
                <IoMdClose />
              </button>
            </div>

            <div className="mt-4 px-5 pb-4">
              {filteredConversations.length > 0 ? (
                filteredConversations.map((c, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      handleConversationClick(c);
                      setShowSearchPopup(false);
                    }}
                    className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100 gap-2 rounded-2xl"
                  >
                    {c.companyPhoto ? (
                      <div className="w-10 h-10 rounded-full mr-3 relative border border-[#ccc]">
                        <img
                          src={c.companyPhoto}
                          alt={c.companyName}
                          className="w-full h-full object-contain rounded-full"
                        />
                      </div>
                    ) : (
                      <div className="h-10 w-10 border rounded-full flex items-center justify-center">
                        <BiSolidUser className="m-0 cursor-pointer w-[30px] h-[30px] text-[#666874]" />
                      </div>
                    )}

                    <div className="flex-1 gap-2">
                      <p
                        title={`${c.jobName}`}
                        className="font-semibold text-sm md:text-base max-w-[190px] md:max-w-[430px] truncate"
                      >
                        {c.jobName}
                      </p>
                      <p
                        className="text-[10px] md:text-sm text-gray-500 max-w-[190px] md:max-w-[430px] truncate"
                        title={c.companyName}
                      >
                        {c.companyName}
                      </p>
                    </div>

                    <span className="text-[8px] md:text-xs text-gray-400">
                      {formatTime(new Date())}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-500 text-lg">
                  No chat with the same name
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 px-4 border-t pt-4">
        {/* Profile Button */}
        <button
          onClick={() => {
            setIsSidebarOpen(false);
            navigate("/profile");
          }}
          className="w-full px-3 py-2 text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-xl transition duration-300 ease-in-out md:text-base text-sm flex items-center gap-2"
        >
          <FaUserCircle className="text-lg" />
          <span className="flex-1 text-left truncate">
            {profile?.name || st.profile}
          </span>
        </button>

        {/* Logout Button */}
        <button
          onClick={() => {
            Cookies.remove("chatToken");
            clearProfile();
            navigate("/login");
          }}
          className="w-full px-3 py-2 text-white bg-red-500 hover:bg-red-600 rounded-xl transition duration-300 ease-in-out md:text-base text-sm flex items-center justify-center gap-1"
        >
          <IoIosLogOut />
          {st.logout}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
